import React, { useRef } from 'react';
import { useAppDispatch } from 'bootstrap/hooks';
import { Footer } from 'components/footer/Footer';
import { pushWithSearchQuery } from 'utils';
import { Information } from 'components/information/Information';
import { Recurly } from 'subscription/recurly/Recurly';
import { Reviews } from 'subscription/components/reviews/Reviews';
import { TrialOffer } from 'subscription/recurly/components/trialoffer/TrialOffer';
import { FacebookReviews } from 'subscription/components/reviews/FacebookReviews';
import { Product } from 'bootstrap/data/product/Product';
import { ReasonToUseTemplate } from '../components/reasontemplate/ReasonToUseTemplate';
import { Header } from '../components/header/Header';
import { FreePaidTable } from './components/freepaidtable/FreePaidTable';
import { FaqAccordion } from './components/faq/FaqAccordion';
import Divider from '../../components/divider/Divider';
import { useSingleProductLogic } from './SingleProductPayment.hook';
import { FeatureSection } from 'subscription/components/featuresection/FeatureIn';
import { PlanSection } from './components/plansection/PlanSection';
import { PlanSectionWrapper } from './components/plansection/PlanSectionWrapper';
import { PlanSelectType } from './components/plansection/PlanSection.constants';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { MarketInfo } from 'subscription/recurly/components/marketinfo/MarketInfo';
import { useFeatures } from 'useFeatures';
import styles from './styles.module.scss';
import { Coupon } from 'subscription/recurly/components/coupon/Coupon';
import { MoneyGuarantee } from './components/moneyguarantee/MoneyGuarantee';
import { isApplePayAvailable } from 'subscription/components/Payments/Payments.utlls';
import { InfoNote } from './components/infoNote/infoNote';
import { ActivationBanner } from 'subscription/components/activationbanner/ActivationBanner';

type Props = {
  defaultProduct: Product;
  products: Map<string, Product>;
};

export const monthlyRenewsPrice = 29.99;

export const SingleProductPayment = ({ defaultProduct, products }: Props) => {
  const dispatch = useAppDispatch();
  const paymentFormRef = useRef<HTMLDivElement>(null);

  const {
    isFacebookReviews,
    isFaq,
    isFreeVsPaid,
    isFeaturedInFlow,
    isSingleSubscriptionFlow,
    isSingleSubscriptionWithTwoPlansFlow,
  } = useFeatures();

  const {
    authToken,
    currencyCode,
    isTrialOfferShown,
    product,
    weekProduct,
    monthProduct,
    quarterProduct,
    yearProduct,
    planType,
    handleGetPlanClick,
  } = useSingleProductLogic({
    defaultProduct,
    products,
    paymentFormRef,
  });

  if (!authToken) {
    dispatch(pushWithSearchQuery('/create-account'));
    return null;
  }

  if (isTrialOfferShown) {
    return <TrialOffer defaultProduct={product} isTrialOfferVisible={true} />;
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.REACT_APP_RECAPTCHA_KEY || ''}
      scriptProps={{
        async: false,
        defer: false,
        appendTo: 'head',
        nonce: undefined,
      }}
    >
      <div className={styles.payment}>
        <div
          className={`
          ${styles.paymentContainer} 
          ${styles.bottom90}
        `}
        >
          <Header />

          {isFeaturedInFlow && <FeatureSection />}

          {!isSingleSubscriptionFlow && <Coupon />}

          {isSingleSubscriptionFlow && <ActivationBanner />}

          <PlanSectionWrapper
            handleGetPlanClick={handleGetPlanClick}
            product={product}
            currencyCode={currencyCode}
            planType={planType}
          >
            {isSingleSubscriptionWithTwoPlansFlow ? (
              <>
                <PlanSection
                  type={PlanSelectType.MONTH}
                  currencyCode={currencyCode}
                  isRenewPriceVisible={true}
                  product={monthProduct}
                  isSelected={planType === PlanSelectType.MONTH}
                />
                <PlanSection
                  type={PlanSelectType.YEAR}
                  currencyCode={currencyCode}
                  product={yearProduct}
                  isSelected={planType === PlanSelectType.YEAR}
                  label="MOST POPULAR"
                />
              </>
            ) : (
              <>
                <PlanSection
                  type={PlanSelectType.MONTH}
                  currencyCode={currencyCode}
                  isRenewPriceVisible={true}
                  product={monthProduct}
                  isSelected={planType === PlanSelectType.MONTH}
                  label="best for lasting results"
                />
                <PlanSection
                  type={PlanSelectType.WEEK}
                  isManual={true}
                  currencyCode={currencyCode}
                  product={weekProduct}
                  isRenewPriceVisible={false}
                  isSelected={planType === PlanSelectType.WEEK}
                  label=""
                />
                <PlanSection
                  type={PlanSelectType.QUARTER}
                  currencyCode={currencyCode}
                  isRenewPriceVisible={true}
                  product={quarterProduct}
                  isSelected={planType === PlanSelectType.QUARTER}
                />
              </>
            )}
          </PlanSectionWrapper>

          {isSingleSubscriptionFlow && (
            <>
              <InfoNote
                product={product}
                planType={planType}
                currencyCode={currencyCode}
              />
              <MoneyGuarantee />
            </>
          )}

          {isFreeVsPaid ? (
            <FreePaidTable product={product} />
          ) : (
            <ReasonToUseTemplate withDivider={false} />
          )}

          <Divider isDividerWithMargin />

          {isFacebookReviews ? <FacebookReviews /> : <Reviews />}

          {!isFacebookReviews && <Divider />}

          {isFaq && <FaqAccordion />}

          <Information />

          {((isSingleSubscriptionFlow && !isApplePayAvailable()) ||
            !isSingleSubscriptionFlow) && (
            <div ref={paymentFormRef} className={styles.paymentForm}>
              <Recurly product={product} />
            </div>
          )}

          {!isSingleSubscriptionFlow && <MarketInfo product={product} />}

          <Footer className={styles.footer} />
        </div>
      </div>
    </GoogleReCaptchaProvider>
  );
};
